import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axiosInstance from '@utils/axiosInstance'
import { CONNECTION_TYPES, FRONT_TO_BACK_CONN_TYPES } from '@constants/connectionTypes'
import { CONNECTION_ENTRY_SOURCES } from '@constants/connectionEntrySources'
import { CONNECTION_SOURCE_TYPES } from '@constants/connectionSourceTypes'
import { SAVE_TYPES } from '@constants/saveTypes'

// import { showToast } from '@utils/toast' // Unused
import { captureEditorSelection } from '@utils/captureEditorSelection'
import { resolvePublicUserId } from '@utils/resolvePublicUserId'
import { createDeduplicationCondition, clearPendingRequest } from '@utils/requestDeduplication'

import { saveNodeEntry } from '@redux/reducers/currentEntryReducer'

const {
  FRONTEND: { SIBLING, EXTERNAL },
} = CONNECTION_TYPES

const { PRIMARY, FOREIGN } = CONNECTION_ENTRY_SOURCES
const { DIRECT, SINGLE_WORD } = CONNECTION_SOURCE_TYPES // DESCRIPTIVE unused

const initialState = {
  connections: [],
  allConnections: [], // All connections across all nodes for Global view
  connectionsLoading: false,
  connectionSourceType: DIRECT,
  connectionTitleInput: '',
  externalConnectionSource: null,
  modalConnectionType: SIBLING,
  selectedPrimarySourceText: '',
  selectedForeignSourceText: '',
}

export const createConnection = createAsyncThunk(
  'connections/create_connection',
  async (
    {
      connection_type,
      current_entry_id,
      primary_entry_id,
      foreign_entry_id,
      primary_source,
      foreign_source,
      source_type,
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      await dispatch(saveNodeEntry({ saveType: SAVE_TYPES.AUTO })).unwrap()

      const response = await axiosInstance.post('api/connections/create_connection', {
        connection_type: connection_type === EXTERNAL ? connection_type : FRONT_TO_BACK_CONN_TYPES[connection_type],
        primary_entry_id,
        foreign_entry_id,
        current_entry_id,
        primary_source,
        foreign_source,
        source_type,
      })

      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data ?? error)
    }
  }
)

export const deleteConnection = createAsyncThunk(
  'connections/delete_connection',
  async (connectionId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`api/connections/delete_connection/${connectionId}`)
      return { connectionId, ...response.data }
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const getSelectedText = createAsyncThunk(
  'connections/getSelectedText',
  async (entrySource, { dispatch, rejectWithValue }) => {
    const captured = captureEditorSelection(entrySource)
    if (!captured?.plainText) {
      return rejectWithValue({ message: 'No text selected' })
    }

    const { plainText, isSingleWord } = captured

    if (entrySource === PRIMARY) {
      dispatch(setSelectedPrimarySourceText(plainText))
    } else if (entrySource === FOREIGN) {
      dispatch(setSelectedForeignSourceText(plainText))
    }

    dispatch(setConnectionSourceType(isSingleWord ? SINGLE_WORD : DIRECT))

    return { entrySource, plainText }
  }
)

export const fetchConnections = createAsyncThunk('connections/', async (entry_id, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`api/connections/${entry_id}`)

    return response.data.connections
  } catch (error) {
    return rejectWithValue(error.response.data)
  }
})

export const fetchAllConnections = createAsyncThunk(
  'connections/fetchAllConnections',
  async (userId, { rejectWithValue }) => {
    try {
      const params = userId != null ? { userId } : {}
      const response = await axiosInstance.get('api/connections/all_connections', { params })
      return response.data.connections
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const fetchPublicConnections = createAsyncThunk(
  'connections/fetchPublicConnections',
  async ({ entryId, userId }, { rejectWithValue }) => {
    const arg = { entryId, userId }
    try {
      const resolvedUserId = resolvePublicUserId(userId)
      const response = await fetch(`/api/connections/public/${entryId}?userId=${resolvedUserId}`)
      if (!response.ok) {
        if (response.status === 204) {
          // No connections found - this is okay
          return []
        }
        throw new Error('Failed to fetch connections')
      }
      const data = await response.json()
      return data.connections || []
    } catch (error) {
      return rejectWithValue({ message: error.message || 'Failed to fetch connections' })
    } finally {
      clearPendingRequest('connections/fetchPublicConnections', arg)
    }
  },
  {
    condition: createDeduplicationCondition('connections/fetchPublicConnections'),
  }
)

export const fetchConnectionsDirect = createAsyncThunk(
  'connections/fetchConnectionsDirect',
  async (entry_id) => {
    const normalizedEntryId =
      typeof entry_id === 'string' || typeof entry_id === 'number' ? String(entry_id).trim() : ''

    // External/invalid ids should never hit the backend.
    if (!normalizedEntryId || normalizedEntryId.startsWith('external-')) {
      clearPendingRequest('connections/fetchConnectionsDirect', entry_id)
      return []
    }

    try {
      const response = await axiosInstance.get(`api/connections/${normalizedEntryId}`)
      return response?.data?.connections || []
    } catch (error) {
      return []
    } finally {
      clearPendingRequest('connections/fetchConnectionsDirect', normalizedEntryId)
    }
  },
  {
    condition: createDeduplicationCondition('connections/fetchConnectionsDirect'),
  }
)

export const updateConnection = createAsyncThunk(
  'connections/update_connection',
  async ({ connectionId, updatedFields, current_entry_id }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.put(`api/connections/update_connection/${connectionId}`, {
        ...updatedFields,
        current_entry_id,
      })
      // TODO maybe here we just make sure to return all the connections in a single call as well
      await dispatch(fetchConnections(current_entry_id))
      return response.data
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

const connectionsSlice = createSlice({
  name: 'connections',
  initialState,
  reducers: {
    resetConnections: (state) => {
      state.connections = []
      state.connectionsLoading = false
      state.error = null
    },
    setModalConnectionType: (state, action) => {
      state.modalConnectionType = action.payload
    },
    setConnectionSourceType: (state, action) => {
      state.connectionSourceType = action.payload
    },
    setSelectedPrimarySourceText: (state, action) => {
      state.selectedPrimarySourceText = action.payload
    },
    setSelectedForeignSourceText: (state, action) => {
      state.selectedForeignSourceText = action.payload
    },
    setConnectionTitleInput: (state, action) => {
      state.connectionTitleInput = action.payload
    },
    clearConnectionSourceSelections: (state) => {
      state.selectedPrimarySourceText = ''
      state.selectedForeignSourceText = ''
      state.connectionSourceType = DIRECT
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createConnection.pending, (state) => {
        state.connectionsLoading = true
      })
      .addCase(createConnection.fulfilled, (state, action) => {
        return {
          ...state,
          connections: action.payload.connections,
          connectionsLoading: false,
          // Force Global View to refetch all_connections (see useGlobalGraphPipeline stage 2a).
          allConnections: [],
        }
      })
      .addCase(createConnection.rejected, (state, action) => {
        return {
          ...state,
          connections: action.payload?.connections ?? state.connections,
          connectionsLoading: false,
          allConnections: [],
        }
      })
      .addCase(deleteConnection.pending, (state) => {
        state.connectionsLoading = true
      })
      .addCase(deleteConnection.fulfilled, (state, action) => {
        state.connectionsLoading = false
        state.connections = state.connections.filter((connection) => connection.id !== action.payload.connectionId)
        state.allConnections = []
      })
      .addCase(deleteConnection.rejected, (state, action) => {
        state.connectionsLoading = false
        state.error = action.payload
      })
      .addCase(fetchConnections.pending, (state) => {
        state.connectionsLoading = true
      })
      .addCase(fetchConnections.fulfilled, (state, action) => {
        return {
          ...state,
          connections: action.payload,
          connectionsLoading: false,
        }
      })
      .addCase(fetchConnections.rejected, (state, action) => {
        state.connectionsLoading = false
        state.error = action.payload
      })
      .addCase(updateConnection.pending, (state) => {
        state.connectionsLoading = true
      })
      .addCase(updateConnection.fulfilled, (state, action) => {
        state.connectionsLoading = false
        // **** HMM this broke some stuff but maybe it was being used for connections modal
        // state.connections = action.payload.connections
      })
      .addCase(updateConnection.rejected, (state, action) => {
        state.connectionsLoading = false
        state.error = action.payload
      })
      .addCase(fetchAllConnections.pending, (state) => {
        state.connectionsLoading = true
        // Avoid mixing a previous graph's edges with the current node list while the new fetch is in flight.
        state.allConnections = []
      })
      .addCase(fetchAllConnections.fulfilled, (state, action) => {
        state.allConnections = action.payload
        state.connectionsLoading = false
      })
      .addCase(fetchAllConnections.rejected, (state, action) => {
        state.connectionsLoading = false
        state.error = action.payload
      })
      .addCase(fetchPublicConnections.pending, (state) => {
        state.connectionsLoading = true
      })
      .addCase(fetchPublicConnections.fulfilled, (state, action) => {
        return {
          ...state,
          connections: action.payload,
          connectionsLoading: false,
        }
      })
      .addCase(fetchPublicConnections.rejected, (state, action) => {
        state.connectionsLoading = false
        state.error = action.payload
        // Set connections to empty array on error instead of undefined
        state.connections = []
      })
  },
})

export const {
  resetConnections,
  setSelectedPrimarySourceText,
  setSelectedForeignSourceText,
  setConnectionTitleInput,
  setConnectionSourceType,
  setModalConnectionType,
  clearConnectionSourceSelections,
} = connectionsSlice.actions

export default connectionsSlice.reducer
